import * as React from "react";

import { isAbortError } from "@/shared/errors";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";

import type { LogManager } from "../LogManager";

export interface UseSearchIndexManagerResult {
  /**
   * Builds the search index. Cancels any in-progress build first.
   */
  buildSearchIndex: () => void;
  /**
   * Disposes the search index. Cancels any in-progress build first.
   */
  disposeSearchIndex: () => Promise<void>;
  /**
   * Cancels an in-progress search index build.
   */
  cancelBuild: () => void;
}

/**
 * Hook that manages search index build/dispose operations with proper
 * abort controller handling.
 */
export function useSearchIndexManager(
  logManager: LogManager | null,
): UseSearchIndexManagerResult {
  const cancelIndexBuildRef = React.useRef<AbortController>(
    new AbortController(),
  );

  const buildSearchIndex = React.useCallback(() => {
    if (logManager === null) {
      return;
    }

    cancelIndexBuildRef.current.abort();
    cancelIndexBuildRef.current = new AbortController();
    logManager.buildSearchIndex(cancelIndexBuildRef.current.signal);
  }, [logManager]);

  const disposeSearchIndex = React.useCallback(async () => {
    if (logManager === null) {
      return;
    }

    cancelIndexBuildRef.current.abort();

    try {
      await logManager.disposeSearchIndex();
    } catch (err) {
      if (!isAbortError(err)) {
        ErrorMonitoringService.captureError(err);
      }
    }
  }, [logManager]);

  const cancelBuild = React.useCallback(() => {
    cancelIndexBuildRef.current.abort();
  }, []);

  return {
    buildSearchIndex,
    disposeSearchIndex,
    cancelBuild,
  };
}
